import { useMutation } from '@tanstack/react-query';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import { PlaceOfResidence, placeOfResidenceBaseSchema, SignUp } from '@/features/ui';
import { confirmationOfApplicationSchema } from '@/features/ui/Steps/ConfirmationOfApplication/model/schemas';
import { PersonalAndPassportData, personalAndPassportDataSchema } from '@/features/ui/Steps/PersonalAndPassportData';

import { useFormDraftPlainTools } from '@/hooks/useFormDraftPlainTools';

import { ROUTES } from '@/router/config';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { UserService, FormDataType, UserRequestDraft } from '@/api/types';

import api from '@/api';

import { SERVICE_CODE, STEP_NUMBER_TO_PAY, STEP_NUMBER_TO_SIGN_UP } from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { reapplicationDocumentsSchema } from './model/schemas';
import { ServiceProps } from './model/types';
import { DataPreview, ExtractRedeliveryResult } from './ui/steps';
import { ReapplicationDocuments } from './ui/steps/ReapplicationDocuments';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Особисті та паспортні дані',
    step: PersonalAndPassportData,
    validationSchema: personalAndPassportDataSchema,
  },
  { title: 'Місце зберігання зброї', step: PlaceOfResidence, validationSchema: placeOfResidenceBaseSchema },
  { title: 'Дані для поновлення', step: ReapplicationDocuments, validationSchema: reapplicationDocumentsSchema },
  { title: 'Підтвердження даних', step: DataPreview, validationSchema: confirmationOfApplicationSchema },
  {
    title: 'Електронний підпис (КЕП)',
    step: SignUp,
  },
  { title: 'Результат', step: ExtractRedeliveryResult },
];

export const Reapplication: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);

  const currentService: UserService = getServiceByCode(SERVICE_CODE) as UserService;
  const { state: locationState } = useLocation() as {
    state: { subject_id?: string } | null;
  };
  const navigate = useNavigate();

  if (!locationState?.subject_id) {
    navigate(-1);
  }

  const subjectId = locationState?.subject_id;

  const [defaultValues, setDefaultValues] = useState<FormDataType>({ subject_id: subjectId });

  const setData = useCallback(
    (data: Nullable<FormDataType>) => {
      setDefaultValues({ subject_id: subjectId, ...(data ?? {}) });
    },
    [subjectId],
  );

  const getDraftExistingMessage = useCallback(async (): Promise<string> => {
    const {
      data: { status },
    } = await api.erz.services.checkReapplicationState(subjectId ?? '');
    return status === 'NEW'
      ? 'У вас вже є створена чернетка на продовження руху по іншій заяві. Ви можете продовжити її або видалити чернетку та створити продовження по цій заяві.'
      : '';
  }, [subjectId]);

  const {
    isLoading: isLoadingDraft,
    onDraftCreate,
    onDraftContinue,
    onDraftSave,
    currentRequestId,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData,
    stepNumberToPay: STEP_NUMBER_TO_PAY,
    stepNumberToSignUp: STEP_NUMBER_TO_SIGN_UP,
    subject_id: subjectId,
    getDraftExistingMessage,
    customCloseDraftModalHandler: () => navigate(ROUTES.USER.REQUESTS.ACTIVE_REQUESTS),
  });

  const { mutateAsync: generatePDF, isPending: isLoadingPdf } = useMutation({
    mutationFn: async ({
      code,
      payload,
    }: {
      code: Parameters<typeof api.services.generatePdf>[0];
      payload: Parameters<typeof api.services.generatePdf>[1];
    }) => {
      const result = await api.services.generatePdf(code, payload);
      serviceFileManager.setAttachment(result);
      return result;
    },
  });

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });

  const handleDraftCreate = async (
    _?: FormDataType,
    specificStep?: (step: number) => void,
  ): Promise<Nullable<string>> =>
    specificStep &&
    String(
      (await onDraftCreate(
        {
          id: currentService.id,
          name: currentService.name,
          code: SERVICE_CODE,
          totalSteps: STEPS.length,
        },
        specificStep,
      )) ?? '',
    );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      });

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, serviceFileManager, submitForm],
  );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
      });
      await generatePDF({ code: SERVICE_CODE, payload: commonPayload });
    },
    [currentRequestId, generatePDF],
  );

  const checkCanSubmit = useCallback(
    (formData: FormDataType) => async (): Promise<boolean> => {
      const {
        data: { canSubmit },
      } = await api.erz.services.canReapplicationSubmit(String(formData?.subject_id) ?? '');
      return canSubmit;
    },
    [],
  );

  const isLoading = isLoadingPdf || isLoadingSubmit || isLoadingDraft;

  return (
    <ServiceFormTemplate
      serviceCode={SERVICE_CODE}
      title={currentService?.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onDraftCreate={handleDraftCreate}
      stepProps={{
        handleGeneratePDF,
        submitForm: handleSubmitForm,
        serviceFileManager,
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
        customNextStepHandler: checkCanSubmit,
        subject_id: subjectId,
      }}
    />
  );
};
